import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { ENumberColType, IDashboardConfigData, INumberCol } from "libraries/interfaces/i-dashboard-config";
import {
    DashboardResourceService, IDashboardRequestUrlParts
} from "libraries/service/resources/dashboard/dashboard-resource.service";
import { SearchParameterService } from "app_dashboard/service/search-parameter.service";
import { Yii } from "libraries/interfaces/Yii";
import { NotificationType } from "libraries/interfaces/INotification";
import { Wording } from "app_dashboard/pages/dashboard/dashboard-page.component";
import { switchMap } from "rxjs/operators";
import { Observable, of, Subscription } from "rxjs";
import { ILoader } from "libraries/component/loader/ILoader";
import { LoaderType } from "libraries/component/loader/loader.component";
import { NameValueType } from "@afidium/ts-facade";
import { LocalizedPipe } from "libraries/pipes/localized/localized.pipe";

interface INumberGraphData {
    key: string;
    total: number;
    val?: number[];
    values?: any[];
    other?: IOtherKey;
}

interface IOtherKey {
    [rdKey: string]: number;
}

@Component({
    selector: 'number-graph',
    template: `
        <notification [type]="notificationType" *ngIf="displayError">
            {{error}}
        </notification>
        <loader *ngIf="loader.active"
                [message]="loader.message"
                [type]="loader.type"></loader>
        <div [id]="id" class="full container" *ngIf="!displayError && !loader.active">
            <div class="graph-title">{{title}}</div>
            <table class="full">
                <tr>
                    <th></th>
                    <th *ngFor="let col of cols" class="number-title">{{col}}</th>
                </tr>

                <tr *ngFor="let row of data">
                    <td>{{row.key}}</td>
                    <td class="number" *ngFor="let value of row.val; let index = index">
                        <ng-container *ngIf="colsType.length && colsType[index] === eAmountType; then amountType else numberType"></ng-container>
                        <ng-template #amountType>{{value | currency: 'EUR'}}</ng-template>
                        <ng-template #numberType>{{value | number}}</ng-template>
                    </td>
                    <ng-container *ngIf="row.values">
                        <td class="number" *ngFor="let key of colsKey; let index = index">
                            <ng-container *ngIf="row.values[key]; else zero">
                                <ng-container *ngIf="colsType.length && colsType[index] === eAmountType; then amountType else numberType"></ng-container>
                                <ng-template #amountType>{{row.values[key] | currency: 'EUR'}}</ng-template>
                                <ng-template #numberType>{{row.values[key] | number}}</ng-template>
                            </ng-container>
                            <ng-template #zero>
                                0
                            </ng-template>
                        </td>
                    </ng-container>
                </tr>
            </table>
        </div>
    `,
    styles: [`
        .full {
            width: 100%;
        }

        .container {
            padding: 0;
        }

        td, th {
            padding: .375rem;
            font-weight: normal;
        }

        .number, .number-title {
            text-align: right;
        }

        .graph-title {
            font-weight: bold;
            padding: .375rem;
        }

        table {
            margin-top: 0;
            table-layout: fixed;
        }
    `],
})
export class NumberGraphComponent implements OnInit, OnDestroy {
    @Input()
    public config: IDashboardConfigData;
    @Input()
    public index: string;

    public id: string;

    public name: string;

    public data: INumberGraphData[];

    public displayError: boolean = false;
    public error: string = "";

    public title: string = "";
    public cols: string[] = [];
    public colsType: ENumberColType[] = [];
    public colsKey: string[] = [];

    public eAmountType: ENumberColType = ENumberColType.AT;

    public currency: string = 'EUR';
    public notificationType: NotificationType = NotificationType.Info;

    public loader: ILoader = {
        active: false,
        message: Yii.t('wui-dashboard-js', 'Recherche en cours...'),
        type: LoaderType.large,
    };

    private wording: Wording = new Wording();
    private subscription: Subscription;

    constructor(
        private readonly searchParameterService: SearchParameterService,
        private readonly dashboardResource: DashboardResourceService,
        private readonly localizedPipe: LocalizedPipe,
    ) {
    }

    public ngOnInit(): void {
        this.id = (Math.random() + 1).toString(36).substring(2);
        this.title = this.replaceAPS(this.config.titleArray || this.config.title);
        if (this.config.numberColumn) {
            this.config.numberColumn
                .forEach((col: INumberCol): void => {
                    this.cols.push(this.replaceAPS(col.name));
                    this.colsType.push(col.type);
                    this.colsKey.push(col.key ?? col.name.toLowerCase());
                });
        }
        this.request();
    }

    public ngOnDestroy(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    public replaceAPS(val: string | NameValueType[]): string {
        if (val && typeof val === "string") {
            return val.replace("***", "'");
        }
        if(Array.isArray(val)){
            return this.localizedPipe.transform(val);
        }
        return "";
    }

    public request(): void {
        this.loader.active = true;
        if (this.subscription && !this.subscription.closed) {
            this.subscription.unsubscribe();
        }

        this.subscription =
            of({
                req: this.config.req,
                type: this.config.type,
                index: this.index,
                ...this.searchParameterService.getSearchParams(),
            })
                .pipe(
                    switchMap((request: IDashboardRequestUrlParts): Observable<any> => {
                        return this.dashboardResource.search(request);
                    }),
                )
                .subscribe({
                    next: (val: any): void => {
                        this.loader.active = false;

                        this.displayError = false;
                        this.draw(val);
                    },
                    error: (err: any): void => {
                        this.loader.active = false;
                        console.error(err)
                        this.displayError = true;
                        this.error = err.data.message;
                    }
                });
    }

    public resize(): void {

    }

    private draw(data: any): void {
        if (data.series) {
            this.data = data.series;
            /*this.data.forEach((row: INumberGraphData): void => {
                if (row.other) {
                    if (row.val === undefined) {
                        row.val = [];
                    }
                    for (let i: number = 1; i < this.cols.length; i++) {
                        const x: string = Object.keys(row.other).find((key: string): boolean => key.toLowerCase() === this.cols[i].toLowerCase());
                        x !== undefined ? row.val.push(row.other[x]) : row.val.push(0);
                    }
                }
            });*/
        } else {
            this.displayError = true;
            this.notificationType = NotificationType.Info;
            this.error = this.wording.noData;
        }
    }

}
